/** biome-ignore-all lint/style/useNamingConvention: camel case */

import cx from 'classnames';
import {ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useResize} from '~/common/hooks/useResize';
import {putAffiliateTags} from '~/common/utils/affiliate_tags';
import {escapeHtml, sanitizeString} from '~/common/utils/sanitize_html';
import {Label} from '~/ViewInvitation/common/Label';
import {
  selectEventType,
  selectIsPremium,
  selectMessageFromHost,
  selectOrigin,
  selectPristineHostMessage,
} from '~/ViewInvitation/selectors/eventDetails';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {pebble} from '~libSass/base/_exports.sass';

import './HostMessage.sass';

const DESKTOP_MAX_HEIGHT = 264; // 16.5rem
const MW_MAX_HEIGHT = 196; // 12.25rem

export const HostMessage = () => {
  const resizing = useResize();
  const isLargishAndUp = useIsLargishAndUp();
  const origin = useSelector(selectOrigin);
  const isPremium = useSelector(selectIsPremium);
  const eventType = useSelector(selectEventType);
  const messageFromHost = useSelector(selectMessageFromHost);
  const pristineHostMessage = useSelector(selectPristineHostMessage);
  const messageTextRef = useRef<HTMLDivElement>(null);
  const readMoreRef = useRef<HTMLDivElement>(null);
  const [textHeight, setTextHeight] = useState<undefined | number>(undefined);
  const [maxHeight, setMaxHeight] = useState<undefined | number>(undefined);
  const [showReadMore, setShowReadMore] = useState(false);
  const initialMaxHeight = isLargishAndUp ? DESKTOP_MAX_HEIGHT : MW_MAX_HEIGHT;
  const expanded = maxHeight !== initialMaxHeight;

  // Recalculate if window resizes or if the host changes the host note
  useEffect(() => {
    setTextHeight(messageTextRef.current?.getBoundingClientRect().height);
    setMaxHeight(initialMaxHeight);
  }, [resizing, initialMaxHeight, messageFromHost]);

  useEffect(() => {
    textHeight && setShowReadMore(initialMaxHeight < textHeight);
  }, [initialMaxHeight, textHeight]);

  const toggleShowMore = useCallback(() => {
    setMaxHeight(expanded ? initialMaxHeight : textHeight);
  }, [expanded, initialMaxHeight, textHeight]);

  const result = isPremium ? 'P' : 'F';

  const amazonTags = {
    tag: 'web-tracking-20',
    asc_campaign: '',
    asc_source: `${eventType}|W|${result}|noreg`,
  };

  let modifiedHostMessage: ReactNode;
  try {
    if (messageFromHost) {
      modifiedHostMessage = putAffiliateTags(sanitizeString(messageFromHost) as string, amazonTags);
    }
  } catch (e) {
    modifiedHostMessage = escapeHtml(messageFromHost);
    evite.error('Failed to put affiliate tags', e);
  }

  if (!messageFromHost) return null;

  return (
    <div>
      <Label value="Host Note" data-qa-id="hoste-note" />
      <div
        id="hostMessage"
        className={cx('details__host-message', {
          'details__host-message--hidden': !messageFromHost && !pristineHostMessage,
        })}
      >
        <div
          ref={readMoreRef}
          className={cx('hosted-by__message', {
            fabric: origin === 21,
            'hosted-by__message--show-gradient': showReadMore && !expanded,
          })}
          data-qa-id="host-message"
          style={{maxHeight: maxHeight}}
        >
          <div className="hosted-by__message__text" ref={messageTextRef}>
            {pristineHostMessage ? (
              <Typography variant="paragraph2" classNames="host-message-placeholder">
                [This is your message to your guest]
              </Typography>
            ) : (
              modifiedHostMessage
            )}
          </div>
        </div>
      </div>
      {showReadMore && (
        <Button
          type="button"
          variant="link"
          tabIndex={-1}
          className="hosted-by__read-more"
          onClick={toggleShowMore}
          data-qa-id={expanded ? 'show-less' : 'show-more'}
        >
          Show {expanded ? 'less' : 'more'}
          <ChevronIcon ratio={0.5} color={pebble} direction={expanded ? 'up' : 'down'} />
        </Button>
      )}
    </div>
  );
};
